import type { ClauseChangeKind, ContractBlockChange, ContractComparison } from "@closer/shared";
import { BLOCK_CHANGE_NAMES, CLAUSE_CHANGE_NAMES } from "@closer/shared";
import { Badge, Button, Card, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { ContractCompareExplanation } from "./ContractCompareExplanation";

/**
 * REDLINE VE MADDE BAZLI FARK GÖRÜNÜMÜ (CLOS-141 `[096]`).
 *
 * ### ⚠ Bu ekran karşılaştırma ÜRETMİYOR
 *
 * Kart: *"bu kart karşılaştırma algoritması üretmez, kaynak versiyonları doğrudan
 * yüklemez ... ham sözleşme içeriğine ayrı erişim yolu açmaz."* Tek veri kaynağı `[095]`'in
 * compare ucu; sözleşme metnini başka bir uçtan çekmek, kartın kapattığı erişim yolunu
 * yeniden açardı.
 *
 * ### ⚠ Fark türü RENGE BAĞLI DEĞİL
 *
 * Kartın kriteri: *"eklenen, silinen, değişen ve taşınan içerik YALNIZ RENGE BAĞLI OLMAYAN
 * etiket, işaret veya açıklamalarla ayrılır."* Her satır metin etiketi taşıyor; renk yalnız
 * ikinci bir ipucu. Renk tek ayırt edici olsaydı ekran okuyucu kullanan ve renk ayrımı
 * yapamayan kullanıcılar farkın türünü hiç öğrenemezdi.
 *
 * ### ⚠ Değişmemiş maddeler VARSAYILAN OLARAK gizli
 *
 * Açık teknik kararın (sanallaştırma/büyük belge sunumu) cevabı bu. Tipik bir revizyonda
 * maddelerin küçük bir kısmı değişir; 1000 maddelik bir sözleşmede değişmeyen 950 maddeyi
 * de çizmek hem okumayı zorlaştırır hem gereksiz DOM üretir. Sanallaştırma kütüphanesi
 * eklemek yerine (yeni bağımlılık ADR ister — refleks 9) görünen küme daraltılıyor;
 * kullanıcı isterse tek düğmeyle açıyor.
 */

const YALNIZ_DEGISENLER: ClauseChangeKind[] = [
  "added",
  "removed",
  "modified",
  "moved",
  "moved_modified",
];

/** Blok satırının işareti — ekranda metinle birlikte, renkten bağımsız okunabilsin diye. */
const BLOK_ISARETI: Record<ContractBlockChange["change"], string> = {
  added: "+",
  removed: "−",
  format_changed: "±",
  unchanged: " ",
};

const BLOK_STILI: Record<ContractBlockChange["change"], string> = {
  added: "border-success/40 bg-success/5",
  removed: "border-danger/40 bg-danger/5 line-through",
  format_changed: "border-warning/40 bg-warning/5",
  unchanged: "border-border-subtle",
};

const MADDE_TONU: Record<ClauseChangeKind, "neutral" | "success" | "warning" | "danger"> = {
  added: "success",
  removed: "danger",
  modified: "warning",
  moved: "neutral",
  moved_modified: "warning",
  unchanged: "neutral",
};

function BlokSatiri({ blok }: { blok: ContractBlockChange }) {
  return (
    <li className={`flex gap-2 rounded-sm border px-2 py-1 text-sm ${BLOK_STILI[blok.change]}`}>
      <span aria-hidden="true" className="select-none font-mono text-fg-faint">
        {BLOK_ISARETI[blok.change]}
      </span>
      <span className="flex-1 text-fg">{blok.text}</span>
      {/* Etiket her satırda: ekran okuyucu türü metinden okuyor, renkten değil. */}
      <span className="shrink-0 text-xs text-fg-soft">{BLOCK_CHANGE_NAMES[blok.change]}</span>
    </li>
  );
}

function MaddeKarti({ madde }: { madde: ContractComparison["clauses"][number] }) {
  return (
    <li id={`madde-${madde.clauseId}`} className="scroll-mt-4">
      <Card
        title={madde.title || "Başlıksız madde"}
        actions={<Badge tone={MADDE_TONU[madde.change]}>{CLAUSE_CHANGE_NAMES[madde.change]}</Badge>}
      >
        {madde.previousTitle ? (
          <p className="mb-2 text-xs text-fg-soft">
            Önceki başlık: <span className="line-through">{madde.previousTitle}</span>
          </p>
        ) : null}
        {madde.change === "moved" || madde.change === "moved_modified" ? (
          <p className="mb-2 text-xs text-fg-soft">
            Sıra {(madde.leftIndex ?? 0) + 1} → {(madde.rightIndex ?? 0) + 1}
          </p>
        ) : null}
        {madde.blocks.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {madde.blocks.map((blok, i) => (
              <BlokSatiri key={`${madde.clauseId}-${i}`} blok={blok} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-fg-soft">Madde içeriği değişmedi.</p>
        )}
      </Card>
    </li>
  );
}

export function ContractComparePage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const left = params.get("left") ?? "";
  const right = params.get("right") ?? "";
  const [degismeyenlerAcik, setDegismeyenlerAcik] = useState(false);

  /**
   * ⚠ Eksik parametreyle sunucuya HİÇ gidilmiyor: iki versiyon seçilmeden sorulacak bir şey
   * yok ve boş bir 422 turu kullanıcıya hiçbir şey kazandırmaz.
   */
  const parametrelerTam = Boolean(left && right);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "karsilastirma", left, right),
    enabled: parametrelerTam,
    queryFn: async () =>
      api<ContractComparison>(`/api/contracts/${id}/versions/compare?left=${left}&right=${right}`, {
        tenantId,
      }),
  });

  /** ⚠ `api()` zarfı `{ data, meta }` olarak döndürüyor; içteki veri bir kat daha derinde. */
  const sonuc = sorgu.data?.data;
  const gorunen = (sonuc?.clauses ?? []).filter(
    (c) => degismeyenlerAcik || YALNIZ_DEGISENLER.includes(c.change),
  );
  const farkVar = (sonuc?.clauses ?? []).some((c) => YALNIZ_DEGISENLER.includes(c.change));

  return (
    <section className="flex w-full flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">Versiyon karşılaştırma</h1>
        <Link
          to={`/app/sozlesmeler/${id}/versiyonlar`}
          className="text-sm text-primary hover:underline"
        >
          Versiyon geçmişine dön
        </Link>
      </div>

      {!parametrelerTam ? (
        <StateView
          state={{
            kind: "validation",
            title: "Karşılaştırma için iki versiyon seçilmelidir",
            description: "Versiyon geçmişinden karşılaştırmak istediğiniz iki versiyonu seçin.",
            retry: false,
          }}
        />
      ) : sorgu.isPending ? (
        <div role="status" aria-label="Karşılaştırma yükleniyor">
          <Skeleton lines={6} />
        </div>
      ) : sorgu.isError ? (
        /**
         * ⚠ Sunucunun kontrollü sebebi OLDUĞU GİBİ gösteriliyor (`[095]` hata sözleşmesi).
         * Ekran kendi metnini uydurmuyor — refleks 4: hata metni şemada tanımlanır.
         */
        <StateView
          state={resolveErrorState(
            sorgu.error instanceof ApiRequestError ? sorgu.error : undefined,
            /** Salt okuma: yeniden deneme güvenli. Kaydın varlığı ise sır — sunucu 404/403 ayrımını zaten gizliyor. */
            { idempotent: true },
          )}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sonuc ? (
        <>
          {/* Kaynak ve YÖN görünür: kullanıcı hangi metnin nereden geldiğini karıştırmamalı. */}
          <Card title="Karşılaştırılan versiyonlar">
            <p className="text-sm text-fg">
              <strong>Versiyon {sonuc.left.versionNumber}</strong> (sol) →{" "}
              <strong>Versiyon {sonuc.right.versionNumber}</strong> (sağ)
            </p>
            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-fg-soft">
              <div>
                <dt className="inline">Eklenen: </dt>
                <dd className="inline font-medium text-fg">{sonuc.summary.added}</dd>
              </div>
              <div>
                <dt className="inline">Silinen: </dt>
                <dd className="inline font-medium text-fg">{sonuc.summary.removed}</dd>
              </div>
              <div>
                <dt className="inline">Değişen: </dt>
                <dd className="inline font-medium text-fg">{sonuc.summary.modified}</dd>
              </div>
              <div>
                <dt className="inline">Taşınan: </dt>
                <dd className="inline font-medium text-fg">{sonuc.summary.moved}</dd>
              </div>
              <div>
                <dt className="inline">Aynı: </dt>
                <dd className="inline font-medium text-fg">{sonuc.summary.unchanged}</dd>
              </div>
            </dl>
          </Card>

          {/*
            AI AÇIKLAMASI (CLOS-207 `[190]`) — deterministik farkın ALTINDA ve AYRI
            başlıkla. Kartın kuralı: açıklama ile hesaplanmış fark birbirine karışmaz.
          */}
          {farkVar ? (
            <ContractCompareExplanation
              contractId={id}
              leftVersionId={left}
              rightVersionId={right}
            />
          ) : null}

          {!farkVar ? (
            <StateView
              state={{
                kind: "validation",
                title: "İki versiyon arasında fark yok",
                description: "Seçilen iki versiyonun madde ve paragrafları aynı.",
                retry: false,
              }}
            />
          ) : (
            <div className="flex flex-col gap-4 lg:flex-row">
              {/* Madde listesi: farka götüren gezinme. Mobilde üstte, geniş ekranda yanda. */}
              <nav aria-label="Değişen maddeler" className="lg:w-64 lg:shrink-0">
                <Card title="Maddeler">
                  <ul className="flex flex-col gap-1">
                    {gorunen.map((c) => (
                      <li key={c.clauseId}>
                        <a
                          href={`#madde-${c.clauseId}`}
                          className="flex items-center justify-between gap-2 rounded-sm px-2 py-1 text-sm text-fg-strong hover:bg-muted"
                        >
                          <span className="truncate">{c.title || "Başlıksız madde"}</span>
                          <span className="shrink-0 text-xs text-fg-soft">
                            {CLAUSE_CHANGE_NAMES[c.change]}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </Card>
              </nav>

              <div className="flex flex-1 flex-col gap-3">
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setDegismeyenlerAcik((a) => !a)}
                  >
                    {degismeyenlerAcik ? "Değişmeyenleri gizle" : "Değişmeyenleri göster"}
                  </Button>
                </div>
                <ul className="flex flex-col gap-3">
                  {gorunen.map((madde) => (
                    <MaddeKarti key={madde.clauseId} madde={madde} />
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
